import { createSlice } from "@reduxjs/toolkit";

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache duration
export const IGNORE_LOCATION_CACHE = false; // false = invalidate cache on location change, true = company-based cache only

const initialState = {
  user: {},
  notificationFilters: {
    type: [],
    isReadOnly: false,
    sortBy: "date",
    notificationsFor: "all",
    leadIds: [],
  },
  email: {
    subject: "",
    body: "",
    email: "",
  },
  // User list cache for UserSelector component
  userListCache: {
    data: [], // Array of user objects
    timestamp: null, // When the data was last fetched
    companyId: null, // To invalidate cache if company changes
    locationId: null, // To invalidate cache if location changes
    version: 0, // Increment to force refresh across all components
  },
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUserSlice: (state, action) => {
      state.user = action.payload.user;
    },
    setnotificationType: (state, action) => {
      state.notificationFilters.type = action.payload;
    },
    setNotificationSortAndFilters: (state, action) => {
      state.notificationFilters.type = action.payload.filters;
      state.notificationFilters.sortBy = action.payload.sortBy;
    },
    setnotificationRead: (state, action) => {
      state.notificationFilters.isReadOnly = action.payload;
    },
    setNoficationFor: (state, action) => {
      state.notificationFilters.notificationsFor = action.payload.notificationsFor;
      state.notificationFilters.leadIds = action.payload.selectedLeads;
    },
    setEmailSubject: (state, action) => {
      state.email.subject = action.payload;
    },
    setEmailBody: (state, action) => {
      state.email.body = action.payload;
    },
    setEmailAddress: (state, action) => {
      state.email.email = action.payload;
    },
    resetEmail: state => {
      state.email = initialState.email;
    },
    // Set user list cache
    setUserListCache: (state, action) => {
      state.userListCache.data = action.payload.users;
      state.userListCache.timestamp = Date.now();
      state.userListCache.companyId = action.payload.companyId;
      state.userListCache.locationId = action.payload.locationId;
    },
    // Invalidate user list cache (force refresh on next load)
    invalidateUserListCache: state => {
      state.userListCache.timestamp = null;
      state.userListCache.version += 1;
    },
    // Clear user list cache completely
    clearUserListCache: state => {
      state.userListCache = initialState.userListCache;
    },
    // Add a single user to cache (when new user is added)
    addUserToCache: (state, action) => {
      const newUser = action.payload;
      const exists = state.userListCache.data.find(u => u.id === newUser.id);
      if (!exists) {
        state.userListCache.data.push(newUser);
        // Keep sorted by fullName
        state.userListCache.data.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
      }
    },
    // Update existing user in cache
    updateUserInCache: (state, action) => {
      const updatedUser = action.payload;
      const index = state.userListCache.data.findIndex(u => u.id === updatedUser.id);
      if (index !== -1) {
        state.userListCache.data[index] = { ...state.userListCache.data[index], ...updatedUser };
        // Re-sort after update
        state.userListCache.data.sort((a, b) => (a.fullName || "").localeCompare(b.fullName || ""));
      }
    },
    // Remove user from cache (when user is deleted/disabled)
    removeUserFromCache: (state, action) => {
      const userId = action.payload;
      state.userListCache.data = state.userListCache.data.filter(u => u.id !== userId);
    },
  },
});

export const {
  setUserSlice,
  setnotificationRead,
  setnotificationType,
  setNotificationSortAndFilters,
  setNoficationFor,
  setEmailBody,
  setEmailSubject,
  setEmailAddress,
  resetEmail,
  setUserListCache,
  invalidateUserListCache,
  clearUserListCache,
  addUserToCache,
  updateUserInCache,
  removeUserFromCache,
} = userSlice.actions;

// Selectors
export const getUser = state => state.user.user;

export const getUserListCache = state => state.user.userListCache.data;

export const getUserListCacheTimestamp = state => state.user.userListCache.timestamp;

export const getUserListCacheVersion = state => state.user.userListCache.version;

// Helper function to check if cache is valid
export const isUserListCacheValid = (state, companyId, locationId) => {
  const cache = state.user.userListCache;
  if (!cache.timestamp || !cache.data.length) return false;
  if (cache.companyId !== companyId) return false;
  // Only check location if IGNORE_LOCATION_CACHE is false
  if (!IGNORE_LOCATION_CACHE && locationId && cache.locationId !== locationId) return false;
  const age = Date.now() - cache.timestamp;
  return age < CACHE_DURATION;
};

export default userSlice.reducer;
